import {
  createContext,
  FunctionComponent,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useApiDispatch } from "./ApiDispatch";
import { apiGetAuth, apiPostAuth } from "@app/backendApi/actions";

const Context = createContext<{
  state: "loading" | "authenticated" | "unauthenticated";
  handleAuthAttempt: (data: {
    username: string;
    password: string;
  }) => Promise<void>;
  error: Error | null;
} | null>(null);

export const AuthContextProvider: FunctionComponent<PropsWithChildren> = ({
  children,
}) => {
  const [state, setState] = useState<
    "loading" | "authenticated" | "unauthenticated"
  >("loading");
  const apiDispatch = useApiDispatch();
  const [authError, setAuthError] = useState<Error | null>(null);

  const checkAuth = useCallback(() => {
    apiDispatch(apiGetAuth())
      .then(() => {
        setState("authenticated");
      })
      .catch(() => {
        setState("unauthenticated");
      });
  }, []);

  useEffect(() => {
    checkAuth();
  }, []);

  // Handler for authentication attempt
  const handleAuthAttempt = ({
    username,
    password,
  }: {
    username: string;
    password: string;
  }) => {
    return apiDispatch(apiPostAuth({ user: username, password }))
      .then(() => {
        setState("authenticated");
        setAuthError(null);
      })
      .catch((err) => {
        setState("unauthenticated");
        setAuthError(err || new Error("Authentication failed"));
        throw err;
      });
  };

  const ctx = useMemo(
    () => ({ state, handleAuthAttempt, error: authError }),
    [state, handleAuthAttempt, authError]
  );

  return <Context.Provider value={ctx}>{children}</Context.Provider>;
};

export const useAuth = () => {
  const ctx = useContext(Context);
  if (!ctx)
    throw new Error("useAuth must be used within an AuthContextProvider");
  return ctx;
};
